import React, { useEffect, useState } from 'react';
import {
  Table, Card, Typography, Tag, Space, Input, Button, Modal, Descriptions,
  Timeline, Statistic, Row, Col, DatePicker, Select, Tabs, Tooltip
} from 'antd';
import {
  ReloadOutlined, EyeOutlined, ClockCircleOutlined,
  CheckCircleOutlined, CloseCircleOutlined, CodeOutlined,
  MessageOutlined, DollarOutlined, GiftOutlined
} from '@ant-design/icons';
import api from '@/api/client';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface PromptService {
  id: number;
  user_id: number;
  username?: string;
  service_type: string;
  status: string;
  input_text?: string;
  output_text?: string;
  params?: any;
  cost_credits: number;
  is_free: boolean;
  api_response_time_ms?: number;
  error_code?: string;
  error_message?: string;
  created_at: string;
  completed_at?: string;
}

interface ServiceHistory {
  id: number;
  event_type: string;
  event_data?: any;
  duration_ms?: number;
  created_at: string;
}

const SERVICE_TYPE_MAP: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  generate: { label: '生成提示词', color: 'blue', icon: <CodeOutlined /> },
  optimize: { label: '优化提示词', color: 'green', icon: <MessageOutlined /> },
  one_click: { label: '一键生成', color: 'purple', icon: <CheckCircleOutlined /> },
  analyze_intent: { label: '意图分析', color: 'orange', icon: <EyeOutlined /> },
  smart_complete: { label: '智能补全', color: 'cyan', icon: <MessageOutlined /> },
};

const STATUS_MAP: Record<string, { color: string; label: string }> = {
  pending: { color: 'default', label: '等待中' },
  processing: { color: 'processing', label: '处理中' },
  completed: { color: 'success', label: '已完成' },
  failed: { color: 'error', label: '失败' },
};

const EVENT_TYPE_MAP: Record<string, { color: string; label: string }> = {
  created: { color: 'default', label: '创建' },
  started: { color: 'processing', label: '开始' },
  api_called: { color: 'blue', label: 'API调用' },
  api_response: { color: 'cyan', label: 'API响应' },
  completed: { color: 'success', label: '完成' },
  failed: { color: 'error', label: '失败' },
};

export default function PromptServicesPage() {
  const [services, setServices] = useState<PromptService[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailModal, setDetailModal] = useState<{ open: boolean; service?: PromptService; history?: ServiceHistory[] }>({ open: false });
  const [stats, setStats] = useState<any>({});
  const [filters, setFilters] = useState({
    userId: '',
    serviceType: '',
    status: '',
    dateRange: null as any,
  });

  const loadServices = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filters.userId) params.userId = filters.userId;
      if (filters.serviceType) params.serviceType = filters.serviceType;
      if (filters.status) params.status = filters.status;

      const [servicesRes, statsRes] = await Promise.all([
        api.get('/prompt-services', { params }),
        api.get('/prompt-services/stats'),
      ]);

      setServices(servicesRes.data.items || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('加载提示词服务失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadServices(); }, [filters]);

  const viewDetail = async (service: PromptService) => {
    try {
      const { data } = await api.get(`/prompt-services/${service.id}`);
      setDetailModal({ open: true, service: data, history: data.history || [] });
    } catch (error) {
      console.error('加载详情失败:', error);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '用户', dataIndex: 'username', width: 100, render: (v: string, r: any) => v || `用户${r.user_id}` },
    {
      title: '服务类型',
      dataIndex: 'service_type',
      width: 120,
      render: (type: string) => {
        const config = SERVICE_TYPE_MAP[type] || { label: type, color: 'default', icon: null };
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (status: string) => {
        const config = STATUS_MAP[status] || { color: 'default', label: status };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: '消耗',
      dataIndex: 'cost_credits',
      width: 80,
      render: (cost: number, r: any) => (
        <Space>
          {r.is_free ? (
            <Tag color="green" icon={<GiftOutlined />}>免费</Tag>
          ) : (
            <span style={{ color: '#ff4d4f' }}>-{cost}</span>
          )}
        </Space>
      ),
    },
    {
      title: '响应时间',
      dataIndex: 'api_response_time_ms',
      width: 100,
      render: (ms?: number) => ms ? `${ms}ms` : '-',
    },
    {
      title: '输入',
      dataIndex: 'input_text',
      ellipsis: true,
      render: (text?: string) => text ? text.substring(0, 50) + (text.length > 50 ? '...' : '') : '-',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      width: 150,
      render: (v: string) => v ? dayjs(v).format('MM-DD HH:mm:ss') : '-',
    },
    {
      title: '操作',
      width: 80,
      render: (_: any, record: PromptService) => (
        <Button size="small" icon={<EyeOutlined />} onClick={() => viewDetail(record)}>
          详情
        </Button>
      ),
    },
  ];

  const renderDetailModal = () => {
    if (!detailModal.service) return null;
    const { service, history } = detailModal;

    return (
      <Modal
        title="提示词服务详情"
        open={detailModal.open}
        onCancel={() => setDetailModal({ open: false })}
        width={900}
        footer={null}
      >
        <Tabs items={[
          {
            key: 'info',
            label: '基本信息',
            children: (
              <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="ID">{service.id}</Descriptions.Item>
                <Descriptions.Item label="用户">{service.username || `用户${service.user_id}`}</Descriptions.Item>
                <Descriptions.Item label="服务类型">
                  {SERVICE_TYPE_MAP[service.service_type]?.label || service.service_type}
                </Descriptions.Item>
                <Descriptions.Item label="状态">
                  <Tag color={STATUS_MAP[service.status]?.color}>
                    {STATUS_MAP[service.status]?.label}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="消耗积分">
                  {service.is_free ? <Tag color="green">免费</Tag> : `-${service.cost_credits}`}
                </Descriptions.Item>
                <Descriptions.Item label="响应时间">
                  {service.api_response_time_ms ? `${service.api_response_time_ms}ms` : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="创建时间">
                  {dayjs(service.created_at).format('YYYY-MM-DD HH:mm:ss')}
                </Descriptions.Item>
                <Descriptions.Item label="完成时间">
                  {service.completed_at ? dayjs(service.completed_at).format('YYYY-MM-DD HH:mm:ss') : '-'}
                </Descriptions.Item>
                {service.error_code && (
                  <Descriptions.Item label="错误代码" span={2}>
                    <Text type="danger">{service.error_code}</Text>
                  </Descriptions.Item>
                )}
                {service.error_message && (
                  <Descriptions.Item label="错误信息" span={2}>
                    <Text type="danger">{service.error_message}</Text>
                  </Descriptions.Item>
                )}
              </Descriptions>
            ),
          },
          {
            key: 'input',
            label: '输入内容',
            children: (
              <Card>
                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                  {service.input_text || '-'}
                </pre>
              </Card>
            ),
          },
          {
            key: 'output',
            label: '输出内容',
            children: (
              <Card>
                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                  {service.output_text || '-'}
                </pre>
              </Card>
            ),
          },
          {
            key: 'history',
            label: '执行历史',
            children: (
              <Timeline mode="left">
                {history?.map((h: ServiceHistory) => (
                  <Timeline.Item
                    key={h.id}
                    color={EVENT_TYPE_MAP[h.event_type]?.color || 'gray'}
                    label={dayjs(h.created_at).format('HH:mm:ss')}
                  >
                    <div>
                      <Text strong>{EVENT_TYPE_MAP[h.event_type]?.label || h.event_type}</Text>
                      {h.duration_ms && (
                        <Text type="secondary" style={{ marginLeft: 8 }}>
                          ({h.duration_ms}ms)
                        </Text>
                      )}
                    </div>
                    {h.event_data && (
                      <pre style={{ fontSize: 12, marginTop: 4, marginBottom: 0 }}>
                        {JSON.stringify(h.event_data, null, 2)}
                      </pre>
                    )}
                  </Timeline.Item>
                ))}
              </Timeline>
            ),
          },
        ]} />
      </Modal>
    );
  };

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>提示词服务管理</Title>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="总调用次数" value={stats.overview?.total_count || 0} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="总消耗积分"
              value={stats.overview?.total_cost || 0}
              valueStyle={{ color: '#ff4d4f' }}
              prefix="-"
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="成功次数" value={stats.overview?.completed_count || 0} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="平均响应时间" value={Math.round(stats.overview?.avg_response_time || 0)} suffix="ms" />
          </Card>
        </Col>
      </Row>

      {/* 筛选 */}
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input.Search
            placeholder="用户ID"
            value={filters.userId}
            onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
            style={{ width: 150 }}
          />
          <Select
            placeholder="服务类型"
            value={filters.serviceType || undefined}
            onChange={(v) => setFilters({ ...filters, serviceType: v })}
            style={{ width: 150 }}
            allowClear
            options={[
              { label: '生成提示词', value: 'generate' },
              { label: '优化提示词', value: 'optimize' },
              { label: '一键生成', value: 'one_click' },
              { label: '意图分析', value: 'analyze_intent' },
              { label: '智能补全', value: 'smart_complete' },
            ]}
          />
          <Select
            placeholder="状态"
            value={filters.status || undefined}
            onChange={(v) => setFilters({ ...filters, status: v })}
            style={{ width: 120 }}
            allowClear
            options={[
              { label: '等待中', value: 'pending' },
              { label: '处理中', value: 'processing' },
              { label: '已完成', value: 'completed' },
              { label: '失败', value: 'failed' },
            ]}
          />
          <Button icon={<ReloadOutlined />} onClick={loadServices}>刷新</Button>
        </Space>
      </Card>

      {/* 服务列表 */}
      <Table
        columns={columns}
        dataSource={services}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20 }}
        scroll={{ x: 1200 }}
      />

      {renderDetailModal()}
    </div>
  );
}
